/**
 * Downloads management module
 * Handles the Downloads tab functionality
 */

import { API } from '../core/api.js';
import { AppState } from '../core/state.js';
import { showNotification } from '../components/notifications.js';
import { escapeHtml } from '../core/utils.js';

// Cache for downloads data - show instantly on tab revisit
let downloadsCache = null;

// Module-level polling interval
let downloadsRefreshInterval = null;

/**
 * Start auto-refresh polling
 */
export function startDownloadsRefresh() {
    if (downloadsRefreshInterval) return; // Already running
    downloadsRefreshInterval = setInterval(() => {
        loadDownloads();
    }, 10000); // Refresh every 10 seconds
}

/**
 * Stop auto-refresh polling
 */
export function stopDownloadsRefresh() {
    if (downloadsRefreshInterval) {
        clearInterval(downloadsRefreshInterval);
        downloadsRefreshInterval = null;
    }
}

/**
 * Load downloads list with cache-first strategy
 * Shows cached data immediately, then updates with fresh data
 */
export async function loadDownloads() {
    // Start auto-refresh when tab is active
    if (AppState.currentTab === 'downloads') {
        startDownloadsRefresh();
    }

    // Show cached data immediately if available
    if (downloadsCache) {
        renderDownloadsList(downloadsCache, true);
    }

    // Fetch fresh data in background
    try {
        const [statsResponse, downloadsResponse] = await Promise.all([
            fetch('/api/stats'),
            fetch('/api/downloads?limit=50')
        ]);

        const stats = await statsResponse.json();
        const data = await downloadsResponse.json();

        // Update cache and render fresh data
        downloadsCache = { stats, data };
        renderDownloadsList(downloadsCache, false);
    } catch (error) {
        console.error('Error loading downloads:', error);
        if (!downloadsCache) {
            const container = document.getElementById('downloads-list');
            if (container) {
                container.textContent = 'Failed to load downloads.';
            }
        }
    }
}

/**
 * Render downloads list from data
 */
function renderDownloadsList(cache, isStale) {
    const { stats, data } = cache;
    const downloads = data.downloads || [];
    const total = data.total || 0;

    // Update storage info header
    const totalStorage = document.getElementById('total-storage');
    if (totalStorage) {
        const updating = isStale ? ' (updating...)' : '';
        totalStorage.textContent = '';
        const span = document.createElement('span');
        span.innerHTML = `Total Storage: <strong>${stats.total_storage_gb} GB</strong> | Downloaded: <strong>${total}</strong> episodes (showing ${downloads.length})${updating}`;
        totalStorage.appendChild(span);
    }

    const container = document.getElementById('downloads-list');
    if (!container) return;

    if (downloads.length === 0) {
        container.textContent = 'No downloads yet. Download some episodes to see them here.';
        return;
    }

    // Build download items
    const fragment = document.createDocumentFragment();
    downloads.forEach(dl => {
        const item = document.createElement('div');
        // Add warning class if file is missing
        item.className = dl.file_exists === false ? 'episode-item card-warning-border' : 'episode-item';

        // File missing badge
        const fileStatusBadge = dl.file_exists === false
            ? '<span class="status-badge status-badge-error">❌ File Missing</span>'
            : '';

        // Determine transcription status badge
        let transcriptionBadge = '';
        const transcriptionStatus = dl.transcription_status || 'pending';
        switch(transcriptionStatus) {
            case 'completed':
                transcriptionBadge = '<span class="status-badge status-badge-success">✓ Transcribed</span>';
                break;
            case 'processing':
                transcriptionBadge = '<span class="status-badge status-badge-warning">⏳ Processing</span>';
                break;
            case 'failed':
                transcriptionBadge = '<span class="status-badge status-badge-error">✗ Failed</span>';
                break;
            case 'not_available':
                transcriptionBadge = '<span class="status-badge status-badge-secondary">⚠ Not Available</span>';
                break;
            default:
                transcriptionBadge = '<span class="status-badge status-badge-secondary">○ Pending</span>';
        }

        // LLM status badge
        let llmBadge = '';
        const llmStatus = dl.llm_status;
        if (llmStatus === 'completed') {
            llmBadge = '<span class="status-badge status-badge-success">🧠 Analyzed</span>';
        } else if (llmStatus === 'processing') {
            llmBadge = '<span class="status-badge status-badge-warning">🧠 Analyzing</span>';
        } else if (llmStatus === 'failed') {
            llmBadge = '<span class="status-badge status-badge-error">🧠 Failed</span>';
        }

        item.innerHTML = `
            <div class="episode-header">
                <div>
                    <div class="episode-title">${escapeHtml(dl.title)}</div>
                    <div style="color: var(--text-secondary); font-size: 0.85rem; margin-top: 0.25rem;">
                        ${escapeHtml(dl.podcast_name)} • ${escapeHtml(dl.file_path)}
                    </div>
                    <div style="margin-top: 0.5rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
                        ${fileStatusBadge}
                        ${transcriptionBadge}
                        ${llmBadge}
                        ${dl.annotation_status === 'completed' ? '<span class="status-badge status-badge-accent">🎯 Annotated</span>' : ''}
                    </div>
                </div>
                <div class="episode-actions">
                    ${dl.annotation_status !== 'completed' ?
                        `<button class="btn btn-small" onclick="annotateEpisode(${dl.id})"
                                style="background: linear-gradient(135deg, var(--success) 0%, #00a8cc 100%);">
                            🎯 Annotate
                        </button>` : ''
                    }
                    <button class="btn btn-small" onclick="fetchTranscription(${dl.id}, ${dl.processed ? 'true' : 'false'})"
                            style="background: linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%);"
                            data-has-transcription="${dl.processed ? 'true' : 'false'}">
                        ${dl.processed ? '🔄 Re-fetch' : '📝 Fetch Transcription'}
                    </button>
                    <button class="btn btn-small btn-secondary" onclick="verifyDownload(${dl.id})">
                        🔍 Verify
                    </button>
                    <button class="btn btn-small btn-secondary" onclick="viewEpisodeDetails(${dl.id})">
                        View
                    </button>
                </div>
            </div>
            <div class="episode-meta">
                <span>📁 ${dl.file_size_mb} MB</span>
                <span>🎵 ${dl.file_format || 'unknown'}</span>
                ${dl.duration_seconds ? `<span>⏱️ ${Math.floor(dl.duration_seconds / 60)} min</span>` : ''}
                <span>📅 ${dl.download_date ? new Date(dl.download_date).toLocaleDateString() : 'Unknown'}</span>
                ${dl.checksum ? `<span title="${dl.checksum}" style="cursor: help;">🔐 SHA256</span>` : ''}
            </div>
        `;
        fragment.appendChild(item);
    });

    container.textContent = '';
    container.appendChild(fragment);
}

/**
 * Annotate episode with AI
 */
export async function annotateEpisode(episodeId, buttonElement = null) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Annotating...';

    try {
        const response = await fetch(`/api/episodes/${episodeId}/annotate`, { method: 'POST' });
        const result = await response.json();

        if (response.ok) {
            button.innerHTML = '✅ Annotated!';

            // Show annotation results
            const notification = document.createElement('div');
            notification.style.cssText = `
                position: fixed;
                bottom: 20px;
                right: 20px;
                background: linear-gradient(135deg, var(--success) 0%, #00a8cc 100%);
                color: white;
                padding: 1.5rem;
                border-radius: 12px;
                box-shadow: 0 10px 30px rgba(0,0,0,0.3);
                z-index: 10000;
                max-width: 400px;
                animation: slideInRight 0.3s;
            `;
            notification.innerHTML = `
                <strong>Annotation Complete!</strong><br>
                📝 Summary generated<br>
                🏷️ ${result.entities_count} entities<br>
                🔑 ${result.keywords_count} keywords<br>
                📚 ${result.books_count} books<br>
                💡 ${result.concepts_count} concepts
            `;
            document.body.appendChild(notification);

            setTimeout(() => {
                notification.style.animation = 'slideOutRight 0.3s';
                setTimeout(() => {
                    notification.remove();
                    loadDownloads();
                }, 300);
            }, 5000);
        } else {
            button.innerHTML = '❌ Failed';
            setTimeout(() => {
                button.innerHTML = '🎯 Annotate';
                button.disabled = false;
            }, 2000);
        }
    } catch (error) {
        console.error('Error annotating episode:', error);
        button.innerHTML = '❌ Error';
        setTimeout(() => {
            button.innerHTML = '🎯 Annotate';
            button.disabled = false;
        }, 2000);
    }
}

/**
 * Verify download integrity
 */
export async function verifyDownload(episodeId, buttonElement = null) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span>';

    try {
        const response = await fetch(`/api/episodes/${episodeId}/verify`, { method: 'POST' });
        const result = await response.json();

        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            bottom: 20px;
            right: 20px;
            background: ${result.valid ? 'var(--success)' : 'var(--error)'};
            color: white;
            padding: 1.5rem;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.3);
            z-index: 10000;
            max-width: 400px;
            animation: slideInRight 0.3s;
        `;

        if (result.valid) {
            notification.innerHTML = `
                <strong>✅ File Verified!</strong><br>
                Checksum and size match expected values.
            `;
        } else if (!result.file_exists) {
            notification.innerHTML = `
                <strong>❌ File Missing!</strong><br>
                File not found at expected location.
            `;
        } else {
            notification.innerHTML = `
                <strong>⚠️ Verification Failed!</strong><br>
                ${!result.checksum_match ? 'Checksum mismatch<br>' : ''}
                ${!result.size_match ? 'File size mismatch' : ''}
            `;
        }

        document.body.appendChild(notification);

        button.innerHTML = '🔍 Verify';
        button.disabled = false;

        setTimeout(() => {
            notification.style.animation = 'slideOutRight 0.3s';
            setTimeout(() => notification.remove(), 300);
        }, 4000);

    } catch (error) {
        console.error('Error verifying download:', error);
        button.innerHTML = '❌';
        setTimeout(() => {
            button.innerHTML = '🔍 Verify';
            button.disabled = false;
        }, 2000);
    }
}
